'use client';
import { useEffect } from 'react';
import * as Sentry from '@sentry/nextjs';

/**
 * Global handler for Firebase-related unhandled promise rejections
 * This prevents Firebase errors in unsupported browsers from crashing the app
 */
export default function FirebaseErrorHandler() {
  useEffect(() => {
    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      const error = event.reason;
      const errorMessage = error?.message || error?.toString() || '';

      // Check if it's a Firebase-related error
      const isFirebaseError =
        errorMessage.includes('messaging') ||
        errorMessage.includes('firebase') ||
        errorMessage.includes('service worker') ||
        errorMessage.includes('OperationError') ||
        errorMessage.includes('Non-recoverable error') ||
        error?.name === 'OperationError';

      if (isFirebaseError) {
        // Prevent the default error handling
        event.preventDefault();

        // Log to console for debugging
        console.warn('Firebase operation failed (non-critical):', {
          message: errorMessage,
          userAgent: navigator.userAgent,
        });

        // Log to Sentry as a warning, not an error
        Sentry.captureException(error, {
          tags: {
            component: 'firebase-error-handler',
            browser: navigator.userAgent,
            errorType: 'firebase-operation-error',
          },
          level: 'warning',
          fingerprint: ['firebase-operation-error'],
        });

        // Don't redirect or crash the app - just gracefully ignore
        return;
      }

      // For non-Firebase errors, let them bubble up normally
    };

    window.addEventListener('unhandledrejection', handleUnhandledRejection);

    return () => {
      window.removeEventListener(
        'unhandledrejection',
        handleUnhandledRejection
      );
    };
  }, []);

  return null;
}
